import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Pause, Play, Loader2, X, Sparkles, Volume2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

/** VoiceConsultDialog — big-mic dictation modal.
 *  Flow:  Idle → Recording (with pause/resume + timer + waveform) → Uploading
 *         → Transcribing → Structuring → Ready-to-apply.
 *  On Apply, calls `onApply(structured)` with the AI-structured JSON so the parent
 *  Consultation page can merge fields into its form. */
export default function VoiceConsultDialog({ open, onOpenChange, patientId, language = "auto", onApply }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | paused | uploading | done | error
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 mic level for reactive UI
  const [transcript, setTranscript] = useState("");
  const [structured, setStructured] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(0);
  const pausedTotalRef = useRef(0);

  const reset = useCallback(() => {
    setPhase("idle");
    setElapsed(0);
    setLevel(0);
    setTranscript("");
    setStructured(null);
    setErrMsg("");
    chunksRef.current = [];
    pausedTotalRef.current = 0;
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    mediaRecorderRef.current = null;
    streamRef.current = null;
    analyserRef.current = null;
    audioCtxRef.current = null;
  }, []);

  // Reset + cleanup whenever the dialog is closed
  useEffect(() => { if (!open) { cleanup(); reset(); } }, [open, cleanup, reset]);
  useEffect(() => () => cleanup(), [cleanup]);

  const startTimer = () => {
    startedAtRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const paused = pausedTotalRef.current;
      setElapsed(Math.floor((Date.now() - startedAtRef.current - paused) / 1000));
    }, 250);
  };

  const startLevelLoop = (stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* level meter is best-effort */ }
  };

  const start = async () => {
    setErrMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
      streamRef.current = stream;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onerror = (e) => { setErrMsg(String(e.error || "Recorder error")); setPhase("error"); };
      rec.start(500);
      setPhase("recording");
      startTimer();
      startLevelLoop(stream);
    } catch (e) {
      setErrMsg(e?.name === "NotAllowedError" ? "Microphone permission denied. Please allow mic access and retry." : (e?.message || "Could not start recording"));
      setPhase("error");
    }
  };

  const pause = () => {
    try {
      mediaRecorderRef.current?.pause();
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      pausedTotalRef.current -= Date.now(); // will be corrected on resume
      setPhase("paused");
    } catch { /* ignore */ }
  };

  const resume = () => {
    try {
      mediaRecorderRef.current?.resume();
      pausedTotalRef.current += Date.now();
      setPhase("recording");
      startTimer();
    } catch { /* ignore */ }
  };

  const stopAndSend = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    rec.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      chunksRef.current = [];
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      streamRef.current = null;
      if (blob.size < 500) { setErrMsg("Too short — please speak for at least a couple of seconds."); setPhase("error"); return; }
      await uploadAudio(blob);
    };
    try { rec.stop(); } catch { /* ignore */ }
    setPhase("uploading");
  };

  const uploadAudio = async (blob) => {
    try {
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      const fd = new FormData();
      fd.append("file", blob, `dictation.${ext}`);
      fd.append("patient_id", patientId);
      if (language) fd.append("language", language);
      const { data } = await api.post("/voice/consultation", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setTranscript(data.transcript || "");
      setStructured(data.structured || null);
      if (!data.structured) {
        toast.warning("Transcription succeeded but AI structuring failed. You can still copy the transcript.");
      } else {
        toast.success("Dictation ready — review and apply");
      }
      setPhase("done");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Voice processing failed";
      setErrMsg(msg);
      setPhase("error");
    }
  };

  const apply = () => {
    if (structured && onApply) onApply(structured);
    onOpenChange(false);
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-[#FDFBF7] border-[#EFECE5]" data-testid="voice-consult-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0A4C36] flex items-center gap-2" style={{ fontFamily: "Instrument Sans" }}>
            <Sparkles size={18} /> Voice consultation
          </DialogTitle>
        </DialogHeader>

        {/* Idle / Recording UI */}
        {(phase === "idle" || phase === "recording" || phase === "paused" || phase === "error") && (
          <div className="py-6 flex flex-col items-center">
            <div className="relative">
              {/* Animated pulse ring, reactive to mic level */}
              <div
                className={`absolute inset-0 rounded-full ${phase === "recording" ? "animate-ping" : ""} bg-[#0A4C36]/20`}
                style={{ transform: `scale(${1 + level * 0.5})`, transition: "transform 80ms linear" }}
              />
              <button
                data-testid="voice-mic-btn"
                onClick={phase === "recording" ? pause : phase === "paused" ? resume : start}
                disabled={phase === "uploading"}
                className={`relative w-32 h-32 rounded-full flex items-center justify-center text-white shadow-lg transition-all
                  ${phase === "recording" ? "bg-[#B14A2A]" : phase === "paused" ? "bg-[#D18A3E]" : "bg-[#0A4C36] hover:bg-[#0e5c42]"}`}
              >
                {phase === "recording" ? <Pause size={40} strokeWidth={2} /> : phase === "paused" ? <Play size={40} strokeWidth={2} /> : <Mic size={40} strokeWidth={2} />}
              </button>
            </div>

            <div className="mt-6 text-3xl font-semibold text-[#1F2A24] tabular-nums" style={{ fontFamily: "Instrument Sans" }} data-testid="voice-elapsed">
              {fmt(elapsed)}
            </div>
            <div className="mt-1 text-xs uppercase tracking-widest text-[#8A9992]">
              {phase === "idle" && "Tap to start dictating"}
              {phase === "recording" && "Recording · tap to pause"}
              {phase === "paused" && "Paused · tap to resume"}
              {phase === "error" && "Error"}
            </div>

            {phase === "idle" && (
              <p className="mt-6 text-sm text-[#47554D] text-center max-w-md leading-relaxed">
                Speak the whole consultation freely — history, examination, vitals, diagnosis,
                medications, follow-up. AI will structure it into your form. English, हिन्दी, தமிழ், తెలుగు, ಕನ್ನಡ.
              </p>
            )}

            {(phase === "recording" || phase === "paused") && (
              <button
                data-testid="voice-stop-btn"
                onClick={stopAndSend}
                className="mt-6 btn-emerald inline-flex items-center gap-2 px-6 py-2"
              >
                <Square size={16} /> Done — process
              </button>
            )}

            {phase === "error" && (
              <div className="mt-6 max-w-md w-full">
                <div className="flex items-start gap-3 rounded-2xl bg-[#F8E7DF] border border-[#E7B29B] p-4" data-testid="voice-error">
                  <AlertCircle size={18} className="text-[#B14A2A] flex-none mt-0.5" />
                  <div className="text-sm text-[#5C3A21]">{errMsg}</div>
                </div>
                <div className="mt-4 flex justify-center">
                  <button onClick={() => { reset(); }} className="btn-outline-emerald" data-testid="voice-retry-btn">Retry</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Uploading / processing */}
        {phase === "uploading" && (
          <div className="py-16 flex flex-col items-center" data-testid="voice-processing">
            <Loader2 size={40} className="text-[#0A4C36] animate-spin" />
            <div className="mt-4 text-sm text-[#47554D]">Transcribing & structuring…</div>
            <div className="mt-1 text-xs text-[#8A9992]">Whisper + Claude Sonnet 4.5 · usually ~4-8s</div>
          </div>
        )}

        {/* Done → preview & apply */}
        {phase === "done" && (
          <div className="py-2" data-testid="voice-result">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0A4C36] mb-2">
              <Volume2 size={14} /> Transcript
            </div>
            <div className="rounded-2xl bg-white border border-[#EFECE5] p-4 text-sm text-[#1F2A24] max-h-40 overflow-y-auto leading-relaxed whitespace-pre-wrap" data-testid="voice-transcript">
              {transcript || <span className="text-[#8A9992] italic">No transcript</span>}
            </div>

            {structured && (
              <>
                <div className="mt-5 flex items-center gap-2 text-xs uppercase tracking-widest text-[#0A4C36] mb-2">
                  <Sparkles size={14} /> AI structured
                </div>
                <div className="rounded-2xl bg-[#E6F0EC] border border-[#0A4C36]/10 p-4 text-sm text-[#1F2A24] space-y-2 max-h-56 overflow-y-auto" data-testid="voice-structured">
                  {structured.chief_complaint && <StructuredRow label="Chief complaint" value={structured.chief_complaint} />}
                  {structured.diagnosis && <StructuredRow label="Diagnosis" value={structured.diagnosis} />}
                  {structured.history?.length > 0 && <StructuredRow label={`History (${structured.history.length})`} value={structured.history.join(" · ")} />}
                  {structured.examination?.length > 0 && <StructuredRow label={`Examination (${structured.examination.length})`} value={structured.examination.join(" · ")} />}
                  {structured.advice?.length > 0 && <StructuredRow label={`Advice (${structured.advice.length})`} value={structured.advice.join(" · ")} />}
                  {structured.medications?.length > 0 && (
                    <StructuredRow
                      label={`Medications (${structured.medications.length})`}
                      value={structured.medications.map((m) => `${m.name}${m.strength ? " " + m.strength : ""}`).join(", ")}
                    />
                  )}
                  {structured.lab_orders?.length > 0 && (
                    <StructuredRow label={`Lab orders (${structured.lab_orders.length})`} value={structured.lab_orders.map((l) => l.name).join(", ")} />
                  )}
                  {structured.follow_up_date && (
                    <StructuredRow label="Follow-up" value={`${structured.follow_up_date} ${structured.follow_up_time || ""} ${structured.follow_up_note || ""}`.trim()} />
                  )}
                </div>
              </>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <button onClick={() => { reset(); }} className="btn-outline-emerald" data-testid="voice-record-again">
                <Mic size={14} /> Record again
              </button>
              <div className="flex gap-2">
                <button onClick={() => onOpenChange(false)} className="pill-tab pill-tab-inactive" data-testid="voice-cancel">
                  <X size={14} /> Cancel
                </button>
                {structured && (
                  <button onClick={apply} className="btn-emerald" data-testid="voice-apply">
                    <Sparkles size={14} /> Apply to consultation
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StructuredRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#5C7568]">{label}</div>
      <div className="text-sm text-[#1F2A24] mt-0.5">{value}</div>
    </div>
  );
}
